import { apiClient } from "@/src/api/apiClient";
import { axiosInstance } from "@/src/api/axiosInstance";
import { PROPERTY_ENDPOINTS } from "@/src/api/endpoints";
import type { ApiRequestConfig } from "@/src/types/api.types";
import type { FloorPlanAnalyzeData } from "@/src/types/floorPlanAnalyzeApi.types";
import type {
  ActivatePropertyResponse,
  CompletePropertyWizardPayload,
  CompletePropertyWizardResponse,
  PropertyDetailData,
  PropertyDto,
  UpdatePropertyWizardPayload,
  UploadPropertyImageResponse,
} from "@/src/types/propertyApi.types";

export const propertiesService = {
  list(config?: ApiRequestConfig): Promise<PropertyDto | PropertyDto[]> {
    return apiClient.get<PropertyDto | PropertyDto[]>(PROPERTY_ENDPOINTS.LIST, config);
  },

  activate(propertyId: string): Promise<ActivatePropertyResponse> {
    return apiClient.patch<ActivatePropertyResponse>(
      PROPERTY_ENDPOINTS.ACTIVATE(propertyId),
    );
  },

  getDetail(propertyId: string): Promise<PropertyDetailData> {
    return apiClient.get<PropertyDetailData>(PROPERTY_ENDPOINTS.DETAIL(propertyId));
  },

  completeWizard(payload: CompletePropertyWizardPayload): Promise<CompletePropertyWizardResponse> {
    return apiClient.post<CompletePropertyWizardResponse>(
      PROPERTY_ENDPOINTS.WIZARD_COMPLETE,
      payload,
    );
  },

  analyzeFloorPlan(file: File): Promise<FloorPlanAnalyzeData> {
    const formData = new FormData();
    formData.append("file", file);
    return apiClient.post<FloorPlanAnalyzeData>(
      PROPERTY_ENDPOINTS.FLOOR_PLAN_ANALYZE,
      formData,
    );
  },

  uploadImage(propertyId: string, file: File): Promise<UploadPropertyImageResponse> {
    const formData = new FormData();
    formData.append("files", file);
    return apiClient.post<UploadPropertyImageResponse>(
      PROPERTY_ENDPOINTS.images(propertyId),
      formData,
    );
  },

  /**
   * Remove property gallery images.
   * - No keys → delete entire gallery (legacy).
   * - With keys → delete only those storage keys (`imageStorageKeys` from property detail).
   */
  async deleteImage(propertyId: string, storageKeys?: string[]): Promise<void> {
    const url = PROPERTY_ENDPOINTS.images(propertyId);
    if (!storageKeys?.length) {
      await apiClient.delete<void>(url);
      return;
    }
    await axiosInstance.delete(url, {
      data: { storageKeys },
    });
  },

  updateWizard(
    propertyId: string,
    payload: UpdatePropertyWizardPayload,
  ): Promise<PropertyDetailData> {
    return apiClient.patch<PropertyDetailData>(
      PROPERTY_ENDPOINTS.WIZARD_UPDATE(propertyId),
      payload,
    );
  },

  async delete(propertyId: string): Promise<{ statusCode: number; message: string }> {
    const response = await axiosInstance.delete<{ statusCode: number; message: string }>(
      PROPERTY_ENDPOINTS.delete(propertyId),
    );
    return response.data;
  },
};
